import { createCodeVerifier, createOpaqueValue, deriveCodeChallenge, safeEqual } from "./pkce";

/**
 * The authorization-code + PKCE flow against Keycloak.
 *
 * WHERE THE TOKEN LIVES, and it is the load-bearing decision here: in memory,
 * in `auth-context`, and NEVER in `localStorage` or a cookie readable by
 * script. This console renders scan evidence, which is hostile markup by
 * construction (web/README, SDD §10.4) — a bearer token in web storage is one
 * successful injection away from being exfiltrated, and it would be a token
 * scoped to a tenant's whole security programme. A reload costs a silent
 * re-authentication instead, which Keycloak's own session makes free.
 *
 * What DOES go to `sessionStorage` is the PKCE verifier, the state and the
 * nonce. They have to survive a full-page redirect, they are useless without
 * the authorization code, they are single-use, and they are deleted the moment
 * the code comes back.
 */

export interface OidcConfig {
  /** e.g. http://localhost:8088/realms/hibrevap */
  issuer: string;
  clientId: string;
  redirectUri: string;
  scope: string;
}

export function oidcConfig(): OidcConfig {
  return {
    // The published issuer is NOT trusted as the place to dial. Keycloak
    // advertises its own frontend URL (`https://auth.localtest.me/...`), which
    // in this deployment is an ingress the browser cannot reach. The
    // deployment says where Keycloak actually is.
    issuer: (import.meta.env.VITE_OIDC_ISSUER ?? "").replace(/\/+$/, ""),
    clientId: import.meta.env.VITE_OIDC_CLIENT_ID ?? "hibrevap-dev",
    redirectUri: import.meta.env.VITE_OIDC_REDIRECT_URI ?? `${window.location.origin}/callback`,
    // `organization` is what carries `org_id` — the claim the gateway derives
    // tenant context from (ADR-048). Without it the token authenticates a
    // person who belongs to no tenant, and every request is refused.
    scope: import.meta.env.VITE_OIDC_SCOPE ?? "openid profile email organization",
  };
}

export function isOidcConfigured(): boolean {
  return oidcConfig().issuer.length > 0;
}

const VERIFIER_KEY = "hibrevap.pkce.verifier";
const STATE_KEY = "hibrevap.pkce.state";
const NONCE_KEY = "hibrevap.pkce.nonce";
const RETURN_KEY = "hibrevap.pkce.return";

export interface TokenSet {
  accessToken: string;
  refreshToken?: string;
  idToken?: string;
  /** Absolute epoch milliseconds. */
  expiresAt: number;
}

export interface Claims {
  sub: string;
  org_id?: string;
  preferred_username?: string;
  name?: string;
  email?: string;
  exp?: number;
  nonce?: string;
}

/**
 * Reads a JWT's payload WITHOUT verifying it.
 *
 * The client does not and must not validate these tokens: the gateway does
 * that, and a browser deciding for itself that a token is good proves nothing.
 * This is for display — a name in the corner, the organisation a request will
 * act in — and for the one check the client owns, which is that the `nonce`
 * coming back is the one it sent.
 */
export function readClaims(jwt: string): Claims | undefined {
  const payload = jwt.split(".")[1];
  if (!payload) return undefined;
  try {
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json) as Claims;
  } catch {
    return undefined;
  }
}

/**
 * Sends the browser to Keycloak. Does not return.
 *
 * `history: "replace"` swaps the current entry for Keycloak's page, so Back
 * from the sign-in form skips the page that sent the visitor there (3.140:
 * /login forwards on arrival, and Back to it would forward them again).
 */
export async function beginLogin(returnTo?: string, prompt?: "none", history: "push" | "replace" = "push"): Promise<void> {
  const config = oidcConfig();
  const verifier = createCodeVerifier();
  const state = createOpaqueValue();
  const nonce = createOpaqueValue();

  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(NONCE_KEY, nonce);
  if (returnTo) sessionStorage.setItem(RETURN_KEY, returnTo);

  const params = new URLSearchParams({
    response_type: "code",
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: config.scope,
    state,
    nonce,
    code_challenge: await deriveCodeChallenge(verifier),
    code_challenge_method: "S256",
  });
  // `prompt=none` is the silent path used on reload: Keycloak answers from its
  // own session if there is one, and returns `login_required` rather than
  // showing a form if there is not.
  if (prompt) params.set("prompt", prompt);

  const url = `${config.issuer}/protocol/openid-connect/auth?${params}`;
  if (history === "replace") window.location.replace(url);
  else window.location.assign(url);
}

export class LoginError extends Error {
  constructor(readonly code: string, message: string) {
    super(message);
    this.name = "LoginError";
  }
}

export interface CompletedLogin {
  tokens: TokenSet;
  claims?: Claims;
  returnTo?: string;
}

/**
 * Handles the redirect back. Consumes the one-time values whatever happens, so
 * a failed attempt cannot leave a verifier lying around for a later one.
 */
export async function completeLogin(search: string): Promise<CompletedLogin> {
  const params = new URLSearchParams(search);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  const expectedState = sessionStorage.getItem(STATE_KEY);
  const expectedNonce = sessionStorage.getItem(NONCE_KEY);
  const returnTo = sessionStorage.getItem(RETURN_KEY) ?? undefined;

  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(NONCE_KEY);
  sessionStorage.removeItem(RETURN_KEY);

  const error = params.get("error");
  if (error) throw new LoginError(error, params.get("error_description") ?? error);

  const code = params.get("code");
  const state = params.get("state");
  if (!code) throw new LoginError("no_code", "The sign-in response carried no authorization code.");

  // CSRF: a code arriving with a state this browser did not mint is an attempt
  // to log this user in as somebody else, and is refused before the exchange.
  if (!expectedState || !state || !safeEqual(expectedState, state)) {
    throw new LoginError("state_mismatch", "The sign-in response did not match this browser's request.");
  }
  if (!verifier) throw new LoginError("no_verifier", "This browser has no record of starting a sign-in.");

  const config = oidcConfig();
  const response = await fetch(`${config.issuer}/protocol/openid-connect/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      client_id: config.clientId,
      redirect_uri: config.redirectUri,
      code,
      code_verifier: verifier,
    }),
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error_description?: string; error?: string };
    throw new LoginError(body.error ?? "token_request_failed", body.error_description ?? "Could not complete sign-in.");
  }

  const body = (await response.json()) as {
    access_token: string; refresh_token?: string; id_token?: string; expires_in?: number;
  };

  const claims = readClaims(body.access_token);
  const idClaims = body.id_token ? readClaims(body.id_token) : undefined;

  // Replay: a token minted for a different authorization request must not be
  // accepted as the answer to this one.
  if (expectedNonce && idClaims?.nonce && !safeEqual(expectedNonce, idClaims.nonce)) {
    throw new LoginError("nonce_mismatch", "The sign-in response was issued for a different request.");
  }

  return {
    tokens: {
      accessToken: body.access_token,
      refreshToken: body.refresh_token,
      idToken: body.id_token,
      expiresAt: Date.now() + (body.expires_in ?? 300) * 1000,
    },
    claims,
    returnTo,
  };
}

/** Exchanges a refresh token. Returns undefined when the session is over. */
export async function refreshTokens(refreshToken: string): Promise<TokenSet | undefined> {
  const config = oidcConfig();
  const response = await fetch(`${config.issuer}/protocol/openid-connect/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "refresh_token",
      client_id: config.clientId,
      refresh_token: refreshToken,
    }),
  });
  if (!response.ok) return undefined;

  const body = (await response.json()) as {
    access_token: string; refresh_token?: string; id_token?: string; expires_in?: number;
  };
  return {
    accessToken: body.access_token,
    refreshToken: body.refresh_token ?? refreshToken,
    idToken: body.id_token,
    expiresAt: Date.now() + (body.expires_in ?? 300) * 1000,
  };
}

/** Ends the Keycloak session too — signing out of the tab alone is not signing out. */
export function logoutUrl(idToken?: string): string {
  const config = oidcConfig();
  const params = new URLSearchParams({ post_logout_redirect_uri: window.location.origin });
  if (idToken) params.set("id_token_hint", idToken);
  else params.set("client_id", config.clientId);
  return `${config.issuer}/protocol/openid-connect/logout?${params}`;
}
